import { useEffect, type RefObject } from 'react';
import type { SearchInventoryItem } from '../useCreateTradeForm';

/**
 * Close the create-trade modal on Escape — extracted from useCreateTradeForm (LR-495).
 *
 * LR-369 review (dp #1): listen on the BUBBLE phase, not capture. A capture
 * listener fires top-down (document → target) before any descendant, so its
 * stopPropagation() killed the Escape onKeyDown of an open SearchableSelect
 * dropdown — pressing Esc inside the dropdown closed the whole modal. On the
 * bubble phase the dropdown input's onKeyDown runs first and its
 * stopPropagation() keeps Esc from reaching this handler, so Esc collapses only
 * the dropdown. With no open dropdown, Esc bubbles up here and closes the modal.
 */
export function useEscapeToClose(
  isOpen: boolean,
  showCloseConfirmation: boolean,
  handleClose: () => void,
) {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseConfirmation) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClose, showCloseConfirmation]);
}

/**
 * Auto-focus the security search input shortly after the modal opens (when no
 * security is picked yet), and clear the dirty flag on close. The 350ms delay
 * matches the modal open animation — the input isn't focusable until after the
 * transition. Extracted from useCreateTradeForm (LR-495).
 */
export function useModalAutoFocus(
  isOpen: boolean,
  selectedInventoryItem: SearchInventoryItem | null,
  securityInputRef: RefObject<HTMLInputElement | null>,
  setHasFormChanges: (v: boolean) => void,
) {
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (securityInputRef.current && !selectedInventoryItem) {
          securityInputRef.current.focus();
        }
      }, 350);
    } else {
      setHasFormChanges(false);
    }
    // ref + stable setter omitted to keep the original [isOpen, selectedInventoryItem] trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedInventoryItem]);
}
